import { z } from 'zod';
import { departmentOfMunicipality, foldName, matchDepartment, matchMunicipality, municipalitiesNamed, splitPlace } from './divipola.js';
/**
 * Requerimientos judiciales (acción de tutela). Quien escribe es un despacho judicial, el accionante
 * es el ciudadano del caso, el plazo lo fija el juez en el auto y la respuesta va al juzgado. Lo propio del caso
 * vive en `cases.court_id`, `cases.judicial_radication` y `cases.metadata.judicial` (`judicialDataSchema`).
 */
/** Esquema de decisión del sistema con el que el motor lee el auto (qué notifica, derechos, medida provisional, vinculación). */
export const JUDICIAL_INTAKE_SCHEMA_CODE = 'JUDICIAL_INTAKE_V1';
/** Qué notifica el despacho. Es la etapa en la que entra (o reentra) el expediente. */
export const JudicialOrderKind = {
    AUTO_ADMISORIO: 'AUTO_ADMISORIO',
    FALLO: 'FALLO',
    IMPUGNACION: 'IMPUGNACION',
    REQUERIMIENTO_CUMPLIMIENTO: 'REQUERIMIENTO_CUMPLIMIENTO',
    DESACATO: 'DESACATO',
    OTRO: 'OTRO',
};
export const JUDICIAL_ORDER_KINDS = Object.values(JudicialOrderKind);
export const JUDICIAL_ORDER_KIND_LABEL = {
    AUTO_ADMISORIO: 'Auto admisorio',
    FALLO: 'Fallo',
    IMPUGNACION: 'Impugnación',
    REQUERIMIENTO_CUMPLIMIENTO: 'Requerimiento de cumplimiento',
    DESACATO: 'Incidente de desacato',
    OTRO: 'Otro',
};
/** Derechos fundamentales que suele invocar una tutela (códigos estables; las etiquetas se muestran en la interfaz). */
export const JUDICIAL_RIGHTS = [
    'VIDA', 'SALUD', 'PETICION', 'DEBIDO_PROCESO', 'MINIMO_VITAL', 'SEGURIDAD_SOCIAL', 'EDUCACION', 'TRABAJO', 'VIVIENDA', 'HABEAS_DATA', 'IGUALDAD', 'LIBERTAD', 'DIGNIDAD', 'OTRO',
];
export const JUDICIAL_RIGHT_LABEL = {
    VIDA: 'Vida', SALUD: 'Salud', PETICION: 'Petición', DEBIDO_PROCESO: 'Debido proceso', MINIMO_VITAL: 'Mínimo vital', SEGURIDAD_SOCIAL: 'Seguridad social',
    EDUCACION: 'Educación', TRABAJO: 'Trabajo', VIVIENDA: 'Vivienda digna', HABEAS_DATA: 'Habeas data', IGUALDAD: 'Igualdad', LIBERTAD: 'Libertad', DIGNIDAD: 'Dignidad humana', OTRO: 'Otro',
};
/** Radicado judicial colombiano: 23 dígitos (despacho, año, consecutivo, instancia), con o sin guiones. */
export const JUDICIAL_RADICATION_RE = /\b(\d{5})[-\s.]?(\d{2})[-\s.]?(\d{2})[-\s.]?(\d{3})[-\s.]?(\d{4})[-\s.]?(\d{5})[-\s.]?(\d{2})\b/;
/** Deja el radicado judicial como lo escriben los despachos: `11001-31-03-001-2026-00123-00`. */
export function normalizeJudicialRadication(raw) {
    const m = raw.match(JUDICIAL_RADICATION_RE);
    return m ? m.slice(1, 8).join('-') : null;
}
export const judicialRadicationSchema = z.string().trim().max(40).transform((v, ctx) => {
    if (!v)
        return v;
    const n = normalizeJudicialRadication(v);
    if (!n)
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'El radicado judicial tiene 23 dígitos (por ejemplo 11001-31-03-001-2026-00123-00)' });
    return n ?? v;
});
/** Un despacho judicial del catálogo (Configuración › Juzgados; también se crea desde «Registrar requerimiento»). El correo lo identifica; ciudad y departamento lo ubican (hay despachos con el mismo nombre en ciudades distintas). */
export const courtInputSchema = z.object({
    name: z.string().trim().min(3).max(200),
    /** Ciudad tal como la escribe el auto; respaldo cuando el municipio no se reconoce. */
    city: z.string().trim().max(120).optional(),
    /** Departamento y municipio en DIVIPOLA (2 y 5 dígitos; el municipio pertenece al departamento). */
    departmentCode: z.string().regex(/^\d{2}$/, 'Código DIVIPOLA de 2 dígitos').optional(),
    municipalityCode: z.string().regex(/^\d{5}$/, 'Código DIVIPOLA de 5 dígitos').optional(),
    email: z.string().trim().toLowerCase().email().max(200),
    /** Código del despacho en la Rama Judicial (los 12 primeros dígitos del radicado), si se conoce. */
    dispatchCode: z.string().trim().max(40).regex(/^[0-9A-Za-z.\-_ ]*$/, 'Solo números, letras, puntos y guiones').optional(),
    active: z.boolean().default(true),
});
const isoDate = z.string().datetime({ offset: true });
/** Lo que `cases.metadata.judicial` guarda. Todo opcional: se va completando etapa a etapa. */
export const judicialDataSchema = z.object({
    /** Qué notificó el despacho la última vez (la etapa del expediente). */
    orderKind: z.enum(JUDICIAL_ORDER_KINDS).optional(),
    /** Derechos invocados por el accionante. */
    rights: z.array(z.string().max(40)).max(20).optional(),
    /** Fecha y hora en que la entidad fue notificada (desde ahí cuenta el término). */
    notifiedAt: isoDate.optional(),
    /** Fecha y hora límite que fijó el juez para contestar o cumplir. Es el plazo del expediente. */
    deadlineAt: isoDate.optional(),
    /** El auto decretó una medida provisional (cumplir de inmediato). */
    provisionalMeasure: z.boolean().optional(),
    /** La entidad es «vinculada» y no «accionada». */
    linked: z.boolean().optional(),
    /** Nombre del accionante tal como viene en el auto (el ciudadano del caso es la persona registrada). */
    petitioner: z.string().max(200).optional(),
    /** El fallo fue en contra de la entidad (ordena algo). */
    rulingAgainst: z.boolean().optional(),
    rulingAt: isoDate.optional(),
    /** Resumen de lo que ordena el fallo. */
    rulingSummary: z.string().max(4000).optional(),
    /** La entidad impugnó el fallo. */
    appealed: z.boolean().optional(),
    /** Cumplimiento del fallo: cuándo y cómo. */
    compliedAt: isoDate.optional(),
    complianceSummary: z.string().max(4000).optional(),
    /** Lo que el motor y el lector del auto sugirieron (prellena «Registrar requerimiento»; alguien lo confirma). */
    suggested: z.record(z.unknown()).optional(),
});
export function judicialDataOf(metadata) {
    const m = (metadata ?? {});
    const parsed = judicialDataSchema.safeParse(m.judicial ?? {});
    return parsed.success ? parsed.data : {};
}
/** Datos judiciales que se editan a mano en el expediente (PATCH /cases/:id). */
export const judicialPatchSchema = z.object({
    courtId: z.string().uuid().nullable().optional(),
    judicialRadication: judicialRadicationSchema.nullable().optional(),
    judicial: judicialDataSchema.partial().optional(),
});
/** Palabras que enlazan un nombre de despacho («JUZGADO QUINTO PENAL MUNICIPAL CON FUNCIÓN DE CONTROL DE GARANTÍAS»). */
const CONNECTORS = new Set(['DE', 'DEL', 'LA', 'LAS', 'LOS', 'EL', 'Y', 'E', 'CON', 'EN', 'PARA', 'SIN']);
const NAME_WORD = /^(?:[A-ZÁÉÍÓÚÑ][A-ZÁÉÍÓÚÑa-záéíóúñ.]*|\d{1,3}[ºª°]?|[-–])$/;
const CITY_WORDS = /^[A-ZÁÉÍÓÚÑ][A-ZÁÉÍÓÚÑa-záéíóúñ.]*(?:\s+[A-ZÁÉÍÓÚÑ][A-ZÁÉÍÓÚÑa-záéíóúñ.]*){0,3}$/;
/** Palabras que siguen a «DE» en el nombre de un despacho y no son una ciudad. */
const NOT_A_CITY = /^(FAMILIA|CIRCUITO|MENORES|GARANT[ÍI]AS|CONOCIMIENTO|EJECUCI[ÓO]N|PENAS|PEQUE[ÑN]AS|TIERRAS|RESTITUCI[ÓO]N|DESCONGESTI[ÓO]N|LA|LO|CONTROL|ADOLESCENTES|ORALIDAD)\b/i;
/**
 * Las menciones de un despacho: «JUZGADO», «TRIBUNAL» o «CORTE» seguido de palabras con mayúscula inicial (o enlaces),
 * hasta la puntuación o el fin de línea; sigue en la línea siguiente solo si la anterior terminó en un enlace («CONTROL
 * DE» ↵ «GARANTÍAS»). «el juzgado el día 18» nunca cuenta porque sus palabras van en minúscula. Vienen de la más larga a
 * la más corta (la más larga suele ser el encabezado en mayúsculas), sin repetidas.
 */
function findCourtNames(t) {
    const found = [];
    const re = /\b(JUZGADO|TRIBUNAL|CORTE)\b/gi;
    let m;
    while ((m = re.exec(t))) {
        const head = m[0];
        if (head !== head.toUpperCase() && head !== head[0] + head.slice(1).toLowerCase())
            continue;
        const words = [head];
        let pos = m.index + head.length;
        let lastConnector = false;
        for (let i = 0; i < 16; i++) {
            const sep = t.slice(pos).match(/^[ \t]*(\n)?[ \t]*/);
            const sepLen = sep?.[0].length ?? 0;
            if (sep?.[1] && !lastConnector)
                break;
            // Una sigla («D.C.») cuenta como palabra; cualquier otro punto termina el nombre.
            const tok = t.slice(pos + sepLen).match(/^(?:(?:[A-ZÁÉÍÓÚÑ]\.){2,}|[^\s.,;:()«»"]+)/);
            if (!tok)
                break;
            const w = tok[0];
            const connector = CONNECTORS.has(w.toUpperCase()) && (w === w.toUpperCase() || w === w.toLowerCase());
            if (!connector && !NAME_WORD.test(w))
                break;
            words.push(w);
            pos += sepLen + w.length;
            lastConnector = connector;
        }
        while (words.length > 1 && CONNECTORS.has(words.at(-1).toUpperCase()))
            words.pop();
        if (words.length < 2)
            continue;
        const name = words.join(' ').replace(/\s+-\s+/g, ' - ');
        const rest = t.slice(pos).match(/^[^\n]*\n([^\n]+)/);
        found.push({ name, words: words.length, nextLine: rest?.[1]?.trim(), end: pos });
    }
    const seen = new Set();
    return found
        .sort((a, b) => b.words - a.words)
        .filter((c) => { const k = c.name.toUpperCase(); if (seen.has(k))
        return false; seen.add(k); return true; })
        .map(({ end: _e, ...c }) => {
        // «… DE MIRAFLORES BOYACÁ»: la ciudad trae el departamento pegado y se separan.
        const raw = cityOf(c);
        const place = splitPlace(raw);
        const city = place?.city ?? raw;
        const department = departmentOf(c) ?? place?.department;
        const municipalityCode = matchMunicipality(city, department)?.code;
        return { ...c, city, department, departmentCode: municipalityCode?.slice(0, 2) ?? matchDepartment(department)?.code, municipalityCode };
    });
}
/** El departamento, cuando la línea siguiente del encabezado lo trae tras la ciudad («TULUÁ - VALLE DEL CAUCA», «Bucaramanga, Santander»). */
function departmentOf(c) {
    if (!c.nextLine)
        return undefined;
    const parts = c.nextLine.split(/\s+[-–]\s+|,\s*|\s+\(|\)/).map((x) => x.trim()).filter(Boolean);
    for (const p of parts.slice(1)) {
        const d = matchDepartment(p);
        if (d)
            return d.name;
    }
    return undefined;
}
/** La ciudad: lo que sigue al último «DE» («… CIVIL MUNICIPAL DE BUCARAMANGA»), nunca una materia («DE FAMILIA»); si no, la línea siguiente del encabezado («TULUÁ - VALLE DEL CAUCA»). */
function cityOf(c) {
    const last = c.name.split(/\s+DE\s+/i).at(-1);
    if (last && last !== c.name && CITY_WORDS.test(last) && !NOT_A_CITY.test(last))
        return last;
    if (c.nextLine) {
        const city = c.nextLine.split(/\s+[-–,]\s+|,/)[0].trim();
        if (CITY_WORDS.test(city) && !NOT_A_CITY.test(city) && !/^(RADICACI|REP[ÚU]BLICA|RAMA|DISTRITO|AUTO|SECRETAR|ACCI[ÓO]N)/i.test(city))
            return city;
    }
    return undefined;
}
const NUMBER_WORDS = {
    un: 1, una: 1, uno: 1, dos: 2, tres: 3, cuatro: 4, cinco: 5, seis: 6, siete: 7, ocho: 8, nueve: 9, diez: 10, doce: 12, quince: 15, veinte: 20, veinticuatro: 24, treinta: 30, cuarenta: 40, setenta: 70,
};
const COMPOUND_WORDS = { 'treinta y seis': 36, 'cuarenta y ocho': 48, 'setenta y dos': 72, 'veinticuatro': 24 };
/** Nombre propio tal como lo escriben los autos: deja las mayúsculas del texto y quita el ruido alrededor. */
function cleanName(raw) {
    // El punto final se quita salvo en una sigla («BOGOTÁ D.C.»).
    return raw.replace(/\s+/g, ' ').trim().replace(/[,;:]+$/, '').replace(/(?<![A-Z])\.$/, '').trim();
}
const TERM_RE = /(?:t[ée]rmino|plazo)\s+(?:perentorio\s+|improrrogable\s+|m[áa]ximo\s+)?(?:de\s+)?(?:(\w+(?:\s+y\s+\w+)?)\s*)?\(?\s*(\d{1,3})?\s*\)?\s*(horas?|d[ií]as?)(\s+h[áa]biles)?|dentro\s+de\s+(?:los|las)?\s*(\w+(?:\s+y\s+\w+)?)\s*\(?\s*(\d{1,3})?\s*\)?\s*(horas?|d[ií]as?)(\s+h[áa]biles)?/gi;
const PETITIONER_RE = /(?:accionante|instaurada\s+por|presentada\s+por|interpuesta\s+por|promovida\s+por|formulada\s+por|impetrada\s+por|incoada\s+por)[:\s]+(?:el\s+|la\s+)?(?:se[ñn]or(?:a)?\s+|ciudadan[oa]\s+)?([A-ZÁÉÍÓÚÑ][A-ZÁÉÍÓÚÑa-záéíóúñ]+(?:\s+(?:de|del|la|las|los|y|DE|DEL|LA|LAS|LOS|Y|[A-ZÁÉÍÓÚÑ][A-ZÁÉÍÓÚÑa-záéíóúñ]+)){1,5})/g;
const PERSON_BEFORE_ID_RE = /([A-ZÁÉÍÓÚÑ]{2,}(?:\s+(?:DE|DEL|LA|LAS|LOS|Y|[A-ZÁÉÍÓÚÑ]{2,})){1,5}),?\s+identificad[oa]/g;
/** Las menciones de cada dato en el texto del auto, para que el motor elija entre ellas. Pura y determinista. */
export function courtOrderCandidates(text) {
    const t = text.replace(/\r/g, '').replace(/[ \t]+/g, ' ');
    const courts = findCourtNames(t).slice(0, 6).map((c) => ({ name: c.name, ...(c.city ? { city: c.city } : {}), ...(c.department ? { department: c.department } : {}), ...(c.departmentCode ? { departmentCode: c.departmentCode } : {}), ...(c.municipalityCode ? { municipalityCode: c.municipalityCode } : {}) }));
    const first = courts[0];
    const places = first?.city && !first.municipalityCode ? municipalitiesNamed(first.city).map((m) => ({ city: m.name, department: departmentOfMunicipality(m.code)?.name ?? '', municipalityCode: m.code })) : [];
    const radications = [...new Set([...t.matchAll(new RegExp(JUDICIAL_RADICATION_RE.source, 'g'))].map((m) => m.slice(1, 8).join('-')))].slice(0, 5);
    const petitioners = new Set();
    for (const m of t.matchAll(PETITIONER_RE)) {
        const name = cleanName(m[1]).replace(/\s+(contra|en contra|identificad[oa]|quien|con)\b.*$/i, '').trim();
        if (name.split(' ').length >= 2)
            petitioners.add(name);
    }
    for (const m of t.matchAll(PERSON_BEFORE_ID_RE)) {
        const name = cleanName(m[1]);
        if (name.split(' ').length >= 2 && !/^(ACCI[ÓO]N|TUTELA|DEMANDA|ACCIONANTE|CIUDADANA|CIUDADANO|SE[ÑN]OR|SE[ÑN]ORA)$/.test(name.split(' ')[0]))
            petitioners.add(name);
    }
    const terms = [];
    for (const m of t.matchAll(TERM_RE)) {
        const word = (m[1] ?? m[5] ?? '').toLowerCase().trim();
        const digits = m[2] ?? m[6];
        const unitWord = m[3] ?? m[7] ?? '';
        const amount = digits ? Number(digits) : COMPOUND_WORDS[word] ?? NUMBER_WORDS[word] ?? NUMBER_WORDS[word.split(' ')[0] ?? ''];
        if (!amount || amount <= 0 || amount > 365)
            continue;
        const unit = /^h/i.test(unitWord) ? 'HOURS' : 'DAYS';
        const around = t.slice(Math.max(0, (m.index ?? 0) - 80), (m.index ?? 0) + m[0].length + 80).replace(/\s+/g, ' ').trim();
        const term = { amount, unit, business: unit === 'DAYS' ? !/calendario|corrientes/i.test(t.slice(m.index ?? 0, (m.index ?? 0) + m[0].length + 30)) : false };
        if (!terms.some((x) => x.term.amount === term.amount && x.term.unit === term.unit))
            terms.push({ term, snippet: around });
        if (terms.length >= 6)
            break;
    }
    return { courts, radications, places, petitioners: [...petitioners].slice(0, 6), terms };
}
/**
 * Lee un auto (texto del PDF) con patrones y sugiere juzgado, radicado judicial, término, accionante, medida provisional y
 * vinculación: el primero de cada lista de candidatos. Con el motor, `applyCourtOrderAnswers` reemplaza esa elección por
 * la del motor. Cuando no encuentra algo lo deja vacío y alguien lo completa en «Registrar requerimiento». Pura y
 * determinista, para probarla sin base de datos.
 */
export function extractCourtOrder(text, opts = {}) {
    const t = text.replace(/\r/g, '').replace(/[ \t]+/g, ' ');
    const c = courtOrderCandidates(t);
    const out = {};
    if (c.courts[0])
        placeCourt(out, c.courts[0]);
    const email = t.match(/[A-Za-z0-9._%+-]+@cendoj\.ramajudicial\.gov\.co/);
    if (email)
        out.courtEmail = email[0].toLowerCase();
    if (c.radications[0])
        out.judicialRadication = c.radications[0];
    if (c.terms[0])
        out.term = c.terms[0].term;
    if (c.petitioners[0])
        out.petitioner = c.petitioners[0];
    const idNumber = t.match(/(?:c[ée]dula\s+de\s+ciudadan[íi]a|c[ée]dula|\bC\.?\s?C\.?|identificad[oa]\s+con)\s*(?:No\.?|N[°º]\.?|n[úu]mero)?\s*([\d][\d.\s]{5,14}\d)/i);
    if (idNumber) {
        const digits = idNumber[1].replace(/\D/g, '');
        if (digits.length >= 6 && digits.length <= 12)
            out.petitionerId = digits;
    }
    if (/medida\s+provisional/i.test(t))
        out.provisionalMeasure = true;
    // Vinculada o accionada: solo con el nombre de la entidad se sabe; sin él no se adivina.
    if (opts.entityName?.trim()) {
        const name = opts.entityName.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+');
        const after = (lead) => new RegExp(`${lead.source}[\\s\\S]{0,160}?${name}`, 'i').test(t);
        if (after(/\bcontra\s+(?:de\s+)?(?:la|el|del|los|las)?/))
            out.linked = false;
        else if (after(/\bvinc[uú]l(?:ese|ase|a|ar|ado|ada|aci[óo]n)\b/))
            out.linked = true;
    }
    if (/incidente\s+de\s+desacato|desacato/i.test(t))
        out.orderKind = 'DESACATO';
    else if (/impugnaci[óo]n/i.test(t) && /concede|admite|tr[áa]mite\s+de\s+la\s+impugnaci[óo]n/i.test(t))
        out.orderKind = 'IMPUGNACION';
    else if (/requi[ée]rase|requerimiento|cumplimiento\s+(?:de\s+)?(?:la\s+)?(?:sentencia|fallo)/i.test(t) && !/admit/i.test(t))
        out.orderKind = 'REQUERIMIENTO_CUMPLIMIENTO';
    else if (/\b(?:sentencia|fallo|resuelve|tutelar|tut[ée]lanse|conceder|neg[aá]r|ampar[aá]r)\b/i.test(t) && /(?:primero|segundo)[.:]/i.test(t) && !/admit/i.test(t))
        out.orderKind = 'FALLO';
    else if (/admit|adm[íi]tase|avóquese|av[óo]quese|notif[íi]quese/i.test(t))
        out.orderKind = 'AUTO_ADMISORIO';
    return out;
}
/** Pone en la lectura un despacho candidato con su ubicación (y quita la ubicación anterior si este no la trae). */
function placeCourt(out, x) {
    out.court = x.name;
    if (x.city)
        out.courtCity = x.city;
    else
        delete out.courtCity;
    if (x.department)
        out.courtDepartment = x.department;
    else
        delete out.courtDepartment;
    if (x.departmentCode)
        out.courtDepartmentCode = x.departmentCode;
    else
        delete out.courtDepartmentCode;
    if (x.municipalityCode)
        out.courtMunicipalityCode = x.municipalityCode;
    else
        delete out.courtMunicipalityCode;
}
/** Campos con los que el motor elige entre candidatos; el valor de cada opción es `K<n>` (índice en la lista) o `NONE`. */
export const COURT_ORDER_PICK_FIELDS = { court: 'courtPick', place: 'placePick', radication: 'radicationPick', petitioner: 'petitionerPick', term: 'termPick', entityRole: 'entityRole' };
export const COURT_ORDER_NONE = 'NONE';
const pickKey = (i) => `K${i + 1}`;
/**
 * Las preguntas que se le hacen al motor sobre un auto: entre los candidatos de cada dato, cuál es el correcto (o
 * ninguno), y si la entidad es la accionada o una vinculada. Cada opción es una afirmación sobre el auto, como pide el
 * motor (docs/decision-engines.md). Sin candidatos de un dato, no se pregunta por él. Nada es obligatorio: un motor que
 * no sepa responder (el de respaldo) no invalida la lectura.
 */
export function courtOrderDecisionSchema(c, opts = {}) {
    const fields = [];
    const pick = (name, label, question, statements, none) => {
        if (!statements.length)
            return;
        fields.push({ name, label, kind: 'enum', required: false, description: question, options: [...statements.map((d, i) => ({ value: pickKey(i), label: `Opción ${i + 1}`, description: d })), { value: COURT_ORDER_NONE, label: 'Ninguno', description: none }] });
    };
    pick(COURT_ORDER_PICK_FIELDS.court, 'Despacho', '¿Qué despacho judicial profiere este auto?', c.courts.map((x) => `El despacho que profiere este auto es «${x.name}»`), 'El despacho que profiere este auto no es ninguno de los nombrados');
    // La ciudad del despacho con homónimos en varios departamentos: el motor dice en cuál está (con uno solo ya lo saben los patrones).
    if (c.places.length > 1)
        pick(COURT_ORDER_PICK_FIELDS.place, 'Municipio del despacho', `¿En qué departamento está el municipio del despacho que profiere este auto?`, c.places.map((p) => `El despacho que profiere este auto está en ${p.city}, ${p.department}`), 'El despacho no está en ninguno de esos municipios');
    // Un solo radicado de 23 dígitos no se discute: solo se pregunta cuando hay varios.
    if (c.radications.length > 1)
        pick(COURT_ORDER_PICK_FIELDS.radication, 'Radicado judicial', '¿Cuál es el número de radicación del proceso de tutela de este auto?', c.radications.map((x) => `El radicado del proceso de tutela es ${x}`), 'El radicado del proceso no es ninguno de los números nombrados');
    pick(COURT_ORDER_PICK_FIELDS.petitioner, 'Accionante', '¿Quién es la persona que presenta la acción de tutela (el accionante)?', c.petitioners.map((x) => `La persona que presenta la tutela (accionante) es ${x}`), 'El accionante no es ninguna de las personas nombradas');
    pick(COURT_ORDER_PICK_FIELDS.term, 'Término', '¿Qué término le concede este auto a la entidad para contestar, informar o cumplir?', c.terms.map((x) => `El término que el auto le da a la entidad para contestar o cumplir es de ${describeCourtTerm(x.term)} («…${x.snippet}…»)`), 'El auto no le fija a la entidad ninguno de esos términos');
    if (opts.entityName?.trim()) {
        const e = opts.entityName.trim();
        fields.push({ name: COURT_ORDER_PICK_FIELDS.entityRole, label: 'Calidad de la entidad', kind: 'enum', required: false, description: `¿En qué calidad entra ${e} a este proceso?`, options: [
                { value: 'ACCIONADA', label: 'Accionada', description: `La acción de tutela se dirige contra ${e}: es la entidad accionada` },
                { value: 'VINCULADA', label: 'Vinculada', description: `${e} entra al proceso como vinculada, sin ser la accionada` },
                { value: 'NINGUNA', label: 'No la menciona', description: `El auto no menciona a ${e} ni como accionada ni como vinculada` },
            ] });
    }
    return { code: 'COURT_ORDER_PICKS', version: '1', fields };
}
/**
 * Aplica lo que respondió el motor sobre los candidatos: con confianza igual o superior a `minConfidence`, su elección
 * reemplaza a la de los patrones (y «ninguno» la borra); si dudó o no respondió una pregunta hecha (`asked`), queda la
 * de los patrones marcada como dudosa. Lo que no se preguntó (un dato sin candidatos, o un radicado único) no es dudoso.
 * Devuelve la lectura y la traza de quién decidió qué.
 */
export function applyCourtOrderAnswers(found, c, decisions, minConfidence, engine, asked) {
    const out = { ...found };
    const trace = { engine, byEngine: [], doubtful: [] };
    const idx = (v) => (typeof v === 'string' && /^K\d+$/.test(v) ? Number(v.slice(1)) - 1 : v === COURT_ORDER_NONE ? -1 : null);
    const wasAsked = (field) => (asked ? asked.includes(field) : true);
    const decide = (field, dataField, hasCandidates, set, clear) => {
        if (!hasCandidates || !wasAsked(field))
            return;
        const d = decisions.find((x) => x.field === field);
        const i = d ? idx(d.value) : null;
        const sure = d?.confidence !== undefined && d.confidence >= minConfidence;
        if (i !== null && sure) {
            if (i >= 0)
                set(i);
            else
                clear();
            trace.byEngine.push(dataField);
        }
        else
            trace.doubtful.push(dataField);
    };
    decide(COURT_ORDER_PICK_FIELDS.court, 'court', c.courts.length > 0, (i) => { const x = c.courts[i]; if (x)
        placeCourt(out, x); }, () => { delete out.court; delete out.courtCity; delete out.courtDepartment; delete out.courtDepartmentCode; delete out.courtMunicipalityCode; });
    // La ubicación vale para el despacho elegido solo si es el de esa ciudad; «ninguno» deja la ciudad sin municipio.
    decide(COURT_ORDER_PICK_FIELDS.place, 'courtMunicipalityCode', c.places.length > 1, (i) => { const p = c.places[i]; if (p && out.courtCity && foldName(out.courtCity) === foldName(p.city)) {
        out.courtMunicipalityCode = p.municipalityCode;
        out.courtDepartmentCode = p.municipalityCode.slice(0, 2);
        out.courtDepartment = p.department;
    } }, () => { delete out.courtMunicipalityCode; });
    decide(COURT_ORDER_PICK_FIELDS.radication, 'judicialRadication', c.radications.length > 0, (i) => { if (c.radications[i])
        out.judicialRadication = c.radications[i]; }, () => { delete out.judicialRadication; });
    decide(COURT_ORDER_PICK_FIELDS.petitioner, 'petitioner', c.petitioners.length > 0, (i) => { if (c.petitioners[i])
        out.petitioner = c.petitioners[i]; }, () => { delete out.petitioner; });
    decide(COURT_ORDER_PICK_FIELDS.term, 'term', c.terms.length > 0, (i) => { if (c.terms[i])
        out.term = c.terms[i].term; }, () => { delete out.term; });
    const role = decisions.find((x) => x.field === COURT_ORDER_PICK_FIELDS.entityRole);
    if (role && role.confidence !== undefined && role.confidence >= minConfidence) {
        if (role.value === 'ACCIONADA')
            out.linked = false;
        else if (role.value === 'VINCULADA')
            out.linked = true;
        else
            delete out.linked;
        trace.byEngine.push('linked');
    }
    else if (role)
        trace.doubtful.push('linked');
    return { found: out, trace };
}
/** Fecha límite a partir del término y de cuándo se notificó; con días hábiles la calcula el API con el calendario. */
export function describeCourtTerm(term) {
    const n = term.amount;
    return term.unit === 'HOURS' ? `${n} ${n === 1 ? 'hora' : 'horas'}` : `${n} ${n === 1 ? 'día' : 'días'}${term.business ? ' hábiles' : ' calendario'}`;
}
//# sourceMappingURL=judicial.js.map